import { useCallback } from "react";
import { createRoot } from "react-dom/client";
import { flushSync } from "react-dom";
import html2pdf from "html2pdf.js";
import PropertyBrochureTemplate from "./PropertyBrochureTemplate";
import { PropertyBrochureData } from "./propertyBrochure.types";
import { ThemeProvider } from "@mui/material/styles";
import theme from "@/shared/theme/theme";
import { Provider } from "react-redux";
import { store } from "@/src-refactor/modules/store/store";

export type { PropertyBrochureData };

const usePropertyBrochurePdf = () => {
  const generateBrochure = useCallback(
    async (data: PropertyBrochureData): Promise<boolean> => {
      if (typeof window === "undefined") {
        console.warn("usePropertyBrochurePdf: hook ejecutado fuera del navegador.");
        return false;
      }

      if (!data?.title) {
        console.error(
          "usePropertyBrochurePdf: se requiere un título para generar el brochure."
        );
        return false;
      }

      const container = document.createElement("div");
      container.style.position = "fixed";
      container.style.left = "-10000px";
      container.style.top = "0";
      container.style.width = "210mm";
      container.style.zIndex = "-1";

      document.body.appendChild(container);

      const root = createRoot(container);

      let element: HTMLElement | null = null;
      let originalStyles:
        | {
            transform: string;
            transformOrigin: string;
            width: string;
            height: string;
          }
        | null = null;

      try {
        flushSync(() => {
          root.render(
            <Provider store={store}>
              <ThemeProvider theme={theme}>
                <PropertyBrochureTemplate data={data} />
              </ThemeProvider>
            </Provider>
          );
        });

        element = container.querySelector("#property-brochure") as HTMLElement | null;

        if (!element) {
          throw new Error("No se pudo crear la plantilla del brochure.");
        }

        await new Promise<void>(resolve => {
          requestAnimationFrame(() => resolve());
        });

        const pxToMm = (px: number) => (px * 25.4) / 96;
        const mmToPx = (mm: number) => (mm / 25.4) * 96;

        const pageHeightMm = 297;
        const pageWidthMm = 210;
        const pageHeightPx = mmToPx(pageHeightMm);

        const brochureHeightPx = element.scrollHeight;

        originalStyles = {
          transform: element.style.transform,
          transformOrigin: element.style.transformOrigin,
          width: element.style.width,
          height: element.style.height
        };

        if (brochureHeightPx > pageHeightPx) {
          const brochureHeightMm = pxToMm(brochureHeightPx);
          const scale = pageHeightMm / brochureHeightMm;

          element.style.transformOrigin = "top left";
          element.style.transform = `scale(${scale})`;
          element.style.width = `${pageWidthMm / scale}mm`;
          element.style.height = `${brochureHeightMm}mm`;
        }

        const safeName = data.title
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, "-")
          .replace(/^-+|-+$/g, "");

        await html2pdf()
          .set({
            margin: [0, 0, 0, 0],
            filename: `brochure-${safeName || "propiedad"}.pdf`,
            image: { type: "jpeg", quality: 0.98 },
            html2canvas: { scale: 2, letterRendering: true, useCORS: true },
            jsPDF: { unit: "mm", format: "a4", orientation: "portrait" }
          })
          .from(element)
          .save();

        return true;
      } catch (error) {
        console.error("Error generando el brochure en PDF:", error);
        return false;
      } finally {
        if (element && originalStyles) {
          element.style.transform = originalStyles.transform;
          element.style.transformOrigin = originalStyles.transformOrigin;
          element.style.width = originalStyles.width;
          element.style.height = originalStyles.height;
        }

        root.unmount();
        document.body.removeChild(container);
      }
    },
    []
  );

  return { generateBrochure };
};

export default usePropertyBrochurePdf;
